import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FilterPrefix, FilterToken, SearchHistoryEntry, SearchMode } from '../types/selectiveEditTypes';
import { getMatchingPrefixes, parseFilterTokens, tokensToDisplayString } from '../utils/parseFilterTokens';
import css from './SmartFilterInput.module.scss';

const PREFIX_META: Record<Exclude<FilterPrefix, 'general'>, { label: string; color: string; hint: string }> = {
  node: { label: 'node:', color: '#0d6efd', hint: 'Filter by node name' },
  step: { label: 'step:', color: '#198754', hint: 'Filter by step content' },
  speaker: { label: 'speaker:', color: '#d86b11', hint: 'Filter by character/speaker' },
  type: { label: 'type:', color: '#6f42c1', hint: 'Filter by step type' },
};

type SmartFilterInputProps = {
  tokens: FilterToken[];
  onTokensChange: (tokens: FilterToken[]) => void;
  searchMode: SearchMode;
  caseSensitive: boolean;
  onSearchModeChange: (mode: SearchMode) => void;
  onCaseSensitiveChange: (value: boolean) => void;
  hasError?: boolean;
  searchHistory: SearchHistoryEntry[];
  onSelectHistory: (entry: SearchHistoryEntry) => void;
  onClearHistory: () => void;
  onCommitToHistory: (raw: string) => void;
  onInputChange?: (value: string) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
};

let nextId = 1;
function makeId(): string {
  return `sfi-${nextId++}`;
}

export function SmartFilterInput({
  tokens,
  onTokensChange,
  searchMode,
  caseSensitive,
  onSearchModeChange,
  onCaseSensitiveChange,
  hasError,
  searchHistory,
  onSelectHistory,
  onClearHistory,
  onCommitToHistory,
  onInputChange,
  inputRef: externalInputRef,
}: SmartFilterInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Sync: when parent sets tokens to empty, clear internal input
  useEffect(() => {
    if (tokens.length === 0) {
      setInputValue('');
      onInputChange?.('');
    }
  }, [tokens.length, onInputChange]);
  const [highlightedSuggestion, setHighlightedSuggestion] = useState(0);
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef ?? internalInputRef;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchModeGroupName = useRef(`sm-${Math.random().toString(36).slice(2, 8)}`).current;

  const suggestions = useMemo(() => getMatchingPrefixes(inputValue), [inputValue]);

  useEffect(() => {
    if (suggestions.length > 0 && inputValue && !inputValue.includes(':')) {
      setShowSuggestions(true);
      setHighlightedSuggestion(0);
    } else {
      setShowSuggestions(false);
    }
  }, [suggestions, inputValue]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
        setShowHistory(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const commitInput = useCallback((raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;

    const parsed = parseFilterTokens(trimmed);
    if (parsed.length > 0) {
      onTokensChange([...tokens, ...parsed]);
      onCommitToHistory(trimmed);
    }
    setInputValue('');
    onInputChange?.('');
    setShowSuggestions(false);
  }, [tokens, onTokensChange, onCommitToHistory, onInputChange]);

  const removeToken = useCallback((id: string) => {
    onTokensChange(tokens.filter((t) => t.id !== id));
  }, [tokens, onTokensChange]);

  const selectSuggestion = useCallback((prefix: FilterPrefix) => {
    setInputValue(`${prefix}:`);
    setShowSuggestions(false);
    inputRef.current?.focus();
  }, [inputRef]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedSuggestion((prev) => (prev + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedSuggestion((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !inputValue.includes(':'))) {
        e.preventDefault();
        selectSuggestion(suggestions[highlightedSuggestion]);
        return;
      }
    }

    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      commitInput(inputValue);
      return;
    }

    if (e.key === 'Backspace' && inputValue === '' && tokens.length > 0) {
      e.preventDefault();
      removeToken(tokens[tokens.length - 1].id);
      return;
    }

    if (e.key === 'Escape') {
      e.stopPropagation();
      e.preventDefault();
      if (showSuggestions || showHistory) {
        setShowSuggestions(false);
        setShowHistory(false);
      } else if (inputValue) {
        setInputValue('');
      } else {
        (e.target as HTMLInputElement).blur();
      }
    }
  }, [showSuggestions, suggestions, highlightedSuggestion, inputValue, tokens, commitInput, removeToken, selectSuggestion, showHistory]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onInputChange?.(e.target.value);
    setShowHistory(false);
  }, [onInputChange]);

  const handleBlur = useCallback(() => {
    // Commit current input on blur if it has content
    if (inputValue.trim()) {
      onCommitToHistory(inputValue.trim());
    }
  }, [inputValue, onCommitToHistory]);

  const placeholder = tokens.length === 0
    ? (searchMode === 'regex' ? 'RegEx pattern... (node:, step:, type:)' : 'Filter... (node:name, step:text, type:choice)')
    : (searchMode === 'regex' ? 'Add filter...' : 'Add filter...');

  return (
    <div className={css.smartFilterRoot} ref={wrapperRef}>
      <div className={css.searchRow}>
        <div className={`${css.chipInputWrapper} ${hasError ? css.chipInputError : ''}`}>
          <div className={css.chipInputInner}>
            {tokens.map((token) => {
              const meta = token.prefix !== 'general' ? PREFIX_META[token.prefix] : null;
              return (
                <span
                  key={token.id}
                  className={css.chip}
                  style={meta ? { borderColor: meta.color } : undefined}
                >
                  {meta && (
                    <span className={css.chipPrefix} style={{ color: meta.color }}>
                      {meta.label}
                    </span>
                  )}
                  <span className={css.chipValue}>{token.value}</span>
                  <button
                    type="button"
                    className={css.chipRemove}
                    onClick={() => removeToken(token.id)}
                    aria-label={`Remove filter ${token.raw}`}
                  >
                    &times;
                  </button>
                </span>
              );
            })}
            <input
              ref={inputRef}
              className={css.chipInput}
              value={inputValue}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              placeholder={placeholder}
              spellCheck={false}
              autoComplete="off"
            />
          </div>

          {/* History toggle */}
          {searchHistory.length > 0 && (
            <button
              type="button"
              className={`${css.historyToggle} ${showHistory ? css.historyToggleOpen : ''}`}
              onMouseDown={(e) => { e.preventDefault(); setShowHistory((v) => !v); setShowSuggestions(false); }}
              aria-label="Toggle search history"
              title="Search history"
            >
              &#9662;
            </button>
          )}

          {/* Clear all */}
          {(tokens.length > 0 || inputValue) && (
            <button
              type="button"
              className={css.clearAll}
              onClick={() => { onTokensChange([]); setInputValue(''); onInputChange?.(''); inputRef.current?.focus(); }}
              aria-label="Clear all filters"
            >
              &times;
            </button>
          )}

          {/* Prefix suggestions dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className={css.suggestionsDropdown}>
              {suggestions.filter((p): p is Exclude<FilterPrefix, 'general'> => p !== 'general').map((prefix, idx) => {
                const meta = PREFIX_META[prefix];
                return (
                  <button
                    key={prefix}
                    type="button"
                    className={`${css.suggestionItem} ${idx === highlightedSuggestion ? css.suggestionHighlighted : ''}`}
                    onMouseDown={(e) => { e.preventDefault(); selectSuggestion(prefix); }}
                  >
                    <span className={css.suggestionPrefix} style={{ color: meta.color }}>{meta.label}</span>
                    <span className={css.suggestionHint}>{meta.hint}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Search history dropdown */}
          {showHistory && searchHistory.length > 0 && (
            <div className={css.suggestionsDropdown}>
              {searchHistory.map((entry, idx) => (
                <button
                  key={`${entry.query}-${idx}`}
                  type="button"
                  className={css.suggestionItem}
                  onMouseDown={(e) => { e.preventDefault(); onSelectHistory(entry); setShowHistory(false); }}
                >
                  <span className={css.historyQuery}>{entry.query}</span>
                  {entry.mode === 'regex' && <span className={css.historyBadge}>regex</span>}
                  {entry.caseSensitive && <span className={css.historyBadge}>Aa</span>}
                </button>
              ))}
              <button
                type="button"
                className={css.historyClear}
                onMouseDown={(e) => { e.preventDefault(); onClearHistory(); setShowHistory(false); }}
              >
                Clear history
              </button>
            </div>
          )}
        </div>

        {/* Search mode controls */}
        <div className={css.modeControls}>
          <label className={css.modeLabel}>
            <input
              type="radio"
              name={searchModeGroupName}
              value="plain"
              checked={searchMode === 'plain'}
              onChange={() => onSearchModeChange('plain')}
            />
            {' Plain'}
          </label>
          <label className={css.modeLabel}>
            <input
              type="radio"
              name={searchModeGroupName}
              value="regex"
              checked={searchMode === 'regex'}
              onChange={() => onSearchModeChange('regex')}
            />
            {' RegEx'}
          </label>
          <label className={css.modeLabel} title="Case sensitive search">
            <input
              type="checkbox"
              checked={caseSensitive}
              onChange={(e) => onCaseSensitiveChange(e.target.checked)}
            />
            {' Aa'}
          </label>
        </div>
      </div>
    </div>
  );
}
